// statusBezugMapping.ts — aus einem erfassten Bezug wird eine Beziehung.
//
// Die Rückfrage „für wen?" (`forms/widgets/StatusBezuege.tsx`) sammelt Paare aus
// Rolle und Gegenkontakt. Gespeichert werden sie NICHT als zweiter Status,
// sondern als ContactRelation — sonst stünde dieselbe Aussage an zwei Stellen
// und liefe auseinander.
//
// Zwei Wege, je nach Rolle:
//
//  1. Die BETEILIGUNGS-Rollen gibt es längst als Firma↔Firma-Verbindungen
//     (`data/firmenverbindungen.ts`, seit T2 mit Richtung und Quote). Sie
//     werden darauf abgebildet statt danebengelegt — die Verbundlogik, die
//     Gesellschafterliste und die Verflechtungsansicht hängen daran.
//
//  2. Alle übrigen Rollen — Verwalter, Betreiber, Reparaturfirma, die externen
//     Zuständigkeiten — erbringen eine FUNKTION für jemanden. Dafür gibt es das
//     bestehende Muster: Beziehungstyp „Mensch fürs Unternehmen" mit der Rolle
//     in `function` und `isExtern`. Genau so speichert die Anlage heute schon
//     die externen Zuständigkeiten (`persistRelations.ts`, kind `verant`).
//
// Rein und ohne Seiteneffekte — in der vitest-Umgebung „node" testbar.

import {
  BETEILIGUNG,
  JOINT_VENTURE,
  MUTTERFIRMA,
  SCHWESTER,
  TOCHTERFIRMA,
  canonicalFirmaConn,
} from "./firmenverbindungen";
import { STATUS_MFU, STATUS_MFU_ALT } from "./sonstigeStatusKatalog";

/**
 * Beteiligungs-Rolle → ROLLE der Firmenverbindung, wie `canonicalFirmaConn`
 * sie erwartet.
 *
 * ACHTUNG, die Blickrichtung dreht sich: Die Statusrolle sagt, was DIESER
 * Kontakt ist („Tochtergesellschaft von X" — ich bin die Tochter). Die
 * Verbindungsrolle sagt, was die ANDERE Firma ist („Mutterfirma" — X ist
 * meine Mutter). Bis zum 21.09.2026 stand hier die wortgleiche Zuordnung
 * („Tochtergesellschaft von" → „Tochterfirma"); sie machte aus der Tochter die
 * Mutter. Aufgefallen ist es nur deshalb nicht, weil der Weg im Bestand noch
 * keine einzige Zeile geschrieben hatte.
 *
 * „Beteiligung außerhalb des Firmenverbundes an" bekommt dieselbe Rolle wie die
 * gewöhnliche Beteiligung: Der Unterschied ist keine andere Art von Anteil,
 * sondern die Frage, ob die Firma zum Verbund gehört — und die beantwortet der
 * Verbund selbst, nicht der Beziehungstyp.
 */
export const BETEILIGUNGS_TYP: Record<string, string> = {
  // Ich halte Anteile an X → „Beteiligung (diese Firma hält Anteile)".
  "Beteiligung an": BETEILIGUNG,
  // Ich bin die Mutter von X → X ist meine Tochter.
  "Muttergesellschaft von": TOCHTERFIRMA,
  // Ich bin die Tochter von X → X ist meine Mutter.
  "Tochtergesellschaft von": MUTTERFIRMA,
  "Schwestergesellschaft von": SCHWESTER,
  // Seit 26.09.2026 getrennt (Anlage „Beteiligungen"): ob Anteile bestehen,
  // ist eine Angabe am Status — die Verbindung bleibt dieselbe.
  "Schwestergesellschaft mit Beteiligung": SCHWESTER,
  "Schwestergesellschaft ohne Beteiligung": SCHWESTER,
  "Joint Venture mit": JOINT_VENTURE,
  "Beteiligung außerhalb des Firmenverbundes an": BETEILIGUNG,
};

/** Der Beziehungstyp, unter dem eine erbrachte Funktion gespeichert wird. */
export const FUNKTIONS_TYP = "Mensch fürs Unternehmen";

/** Der Beziehungstyp eines Arbeitsverhältnisses: Firma = src, Person = dest. */
export const ARBEITS_TYP = "Mitarbeiter";

/**
 * Rollen, die ein ARBEITSVERHÄLTNIS meinen — und auf welcher Seite dieser
 * Kontakt dabei steht.
 *
 * Beide hingen bis zum 21.09.2026 am Rückfall weiter unten und wären als
 * „Mensch fürs Unternehmen, Funktion ‚Mensch im Unternehmen', extern"
 * gespeichert worden — ein Angestellter als externer Dienstleister seiner
 * eigenen Firma.
 */
const ARBEITSROLLEN: Record<string, "arbeitgeber" | "arbeitnehmer"> = {
  "Arbeitgeber eines Privat-Kontaktes": "arbeitgeber",
  "Mensch im Unternehmen": "arbeitnehmer",
};

/**
 * Die Rolle „Verantwortung fürs Unternehmen" (Speicherwert bis 29.09.2026
 * „Mensch fürs Unternehmen"): Der Beziehungstyp „Mensch fürs Unternehmen" ist
 * die Aussage. Welche Verantwortung es ist, erfasst die Maske weiter unten —
 * hier die Rolle selbst als `function` einzutragen, ergäbe „Mensch fürs
 * Unternehmen: Verantwortung fürs Unternehmen".
 *
 * Achtung, zwei Dinge mit (früher) gleichem Namen: der STATUS heißt seit dem
 * 29.09.2026 „Verantwortung fürs Unternehmen", der BEZIEHUNGSTYP bleibt
 * [[FUNKTIONS_TYP]].
 */
const ROLLEN_SIND_DER_TYP: ReadonlySet<string> = new Set([STATUS_MFU, STATUS_MFU_ALT]);

export type BezugRelation = {
  /** Name des Beziehungstyps — das Backend löst ihn auf. */
  relationType: string;
  /** Die konkrete Rolle, wenn der Typ generisch ist. */
  function?: string;
  isExtern?: boolean;
};

/**
 * Was für eine Rolle gespeichert wird.
 *
 * Die Beteiligungs-Rollen tragen ihre Aussage im TYP, alle anderen in
 * `function`. Eine Rolle ohne Zuordnung gibt es nicht: Der Rückfall ist der
 * Funktions-Typ, damit ein neu ergänzter Katalogeintrag gespeichert wird und
 * nicht still verschwindet.
 *
 * Bei den Beteiligungs-Rollen ist der Typ hier der KANONISCHE — es wird nie
 * eine „Mutterfirma"-Zeile gespeichert (siehe `firmenverbindungen.ts`).
 */
export const bezugZuRelation = (rolle: string): BezugRelation => {
  const verbindung = BETEILIGUNGS_TYP[rolle];
  if (verbindung) return { relationType: canonicalFirmaConn(verbindung, 1, 2).relationType };
  if (ARBEITSROLLEN[rolle]) return { relationType: ARBEITS_TYP };
  if (ROLLEN_SIND_DER_TYP.has(rolle)) return { relationType: FUNKTIONS_TYP };
  return { relationType: FUNKTIONS_TYP, function: rolle, isExtern: true };
};

/**
 * In welcher RICHTUNG die Beziehung gespeichert wird.
 *
 * `relationType` sagt überall im Bestand, was `contactDest` für `contactSrc`
 * ist. Für die Bezüge heißt das:
 *
 *  · Beteiligungs-Rollen → die kanonische Zeile aus `canonicalFirmaConn`
 *    (Eltern = src bei „Tochterfirma", Gesellschaft = src bei „Beteiligung").
 *  · Arbeitgeber → die Firma ist src, die Person dest.
 *  · Erbrachte Funktion → der, FÜR den sie erbracht wird, ist src; wer sie
 *    erbringt, ist dest. So stehen alle 956 Bestandszeilen („Firma → Person").
 *
 * Bis zum 21.09.2026 lief jeder Bezug mit „dieser Kontakt = src": Der
 * Steuerberater stand als Quelle, die Firma als sein „Mensch fürs Unternehmen".
 *
 * `selbstId` ist der Kontakt, der gerade angelegt wird, `gegenueberId` die
 * Antwort auf „für wen?".
 */
export const bezugKante = (
  rolle: string,
  selbstId: number,
  gegenueberId: number,
): { contactSrcId: number; contactDestId: number } => {
  const verbindung = BETEILIGUNGS_TYP[rolle];
  if (verbindung) {
    const k = canonicalFirmaConn(verbindung, selbstId, gegenueberId);
    return { contactSrcId: k.contactSrcId, contactDestId: k.contactDestId };
  }
  if (ARBEITSROLLEN[rolle] === "arbeitgeber") {
    return { contactSrcId: selbstId, contactDestId: gegenueberId };
  }
  return { contactSrcId: gegenueberId, contactDestId: selbstId };
};
